import { useEffect, useMemo, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { BlackListService } from '../../../services/blacklist.service';
import { AlertService } from '../../../services/alert.service';
import { Dropzone, ExtFile } from '@dropzone-ui/react';
import Button from '../../../components/forms/button.component';
import Switch from '../../../components/forms/switch.component';
import Select from 'react-select';
import Input from '../../../components/forms/input.component';
import LogData from '../../../components/logs/log-data.component';
import useRequest from '../../../hooks/use-request.hook';
import useInputValidation from '../../../hooks/use-input-validation.hook';
import style from './blacklist.module.scss';
import Title from '../../../components/forms/titles.component';
import InvalidFeedback from '../../../components/forms/invalid-feedback.component';
import Navigate from '../../../components/cards/header-navigate.component';

function EditBlackList() {
  const { state } = useLocation();
  const navigate = useNavigate();
  const payload = state?.payload;
  const { t } = useTranslation();
  const { tenDigitsRegex, handleOnlyNumbers } = useInputValidation();
  const [term, setTerm] = useState<string>('');
  const { call, isRequesting } = useRequest();
  const [rows, setRows] = useState<any>([]);
  const [currentPage, setCurrentPage] = useState(0);
  const [totalRows, setTotalRows] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState<number>(25);
  const [totalRegisters, setTotalRegisters] = useState(payload?.totalNumbers);
  const [type, setType] = useState(false);

  const options = [
    { value: 10, label: '10' },
    { value: 15, label: '15' },
    { value: 25, label: '25' },
    { value: 50, label: '50' },
    { value: 75, label: '75' },
    { value: 100, label: '100' }
  ];

  const defaultValues = {
    file: '',
    blacklist: '',
    registers: 25,
    type: false,
    phone_number: '',
    name: ''
  };

  const {
    register,
    handleSubmit,
    reset,
    watch,
    control,
    setValue,
    resetField,
    formState: { errors, isValid, isSubmitting }
  } = useForm({
    mode: 'onChange',
    defaultValues
  });

  const file = watch('file');

  const updateFiles = (incomingFiles: ExtFile[]) => {
    const allowedExtensions = ['.csv'];
    const file = incomingFiles[0]?.file as File | null;

    if (file && allowedExtensions.includes(`.${file.name.split('.').pop()}`)) {
      setValue('file', file as any);
    } else {
      AlertService.toastError(t('blacklist-page.file-error'));
      setValue('file', '');
    }
  };

  const removeFile = (event: React.MouseEvent<HTMLAnchorElement, MouseEvent>) => {
    event.stopPropagation();
    setValue('file', '');
  };

  const columns = [
    {
      name: t('blacklist-page.phone-number-label'),
      sortable: false,
      cell: (row: any) => {
        return <h5>{row.number}</h5>;
      }
    },
    {
      name: t('general.acctions'),
      sortable: false,
      center: true,
      exportable: false,
      cell: (row: any) => {
        return (
          <div className={`d-flex text-nowrap ${style.btnContainer}`}>
            <button
              type="button"
              className="btn btn-light-danger"
              onClick={() => deleteNumberBlackList(row, 'deleteNumber-96754323465')}
            >
              <i className="ki-duotone ki-trash fs-2">
                <span className="path1"></span>
                <span className="path2"></span>
                <span className="path3"></span>
                <span className="path4"></span>
                <span className="path5"></span>
              </i>
              {t('general.delete')}
            </button>
          </div>
        );
      }
    }
  ];

  const getFile = async (page: number) => {
    const response = await call(() =>
      BlackListService.getCSVs(payload?._id, page + 1, rowsPerPage)
    );
    if (response) {
      setRows(response.numbers);
      setTotalRows(payload?.totalNumbers || 0);
      setTotalRegisters(response.metadata.totalItems);
    } else {
      setRows([]);
    }
  };

  const handleDownloadInvalidNumbers = (numbers: string[]) => () => {
    if (!numbers || numbers.length === 0) return;

    const csvContent = numbers.map((num: string) => `"${num}"`).join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);

    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', 'numeros_invalidos.csv');
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const submitNumber = async (data: any, buttonId: string) => {
    if (type) {
      if (!file) {
        AlertService.toastWarning(t('blacklist-page.warning-csv-file'));
        return;
      }
      try {
        const confirmed = await AlertService.question(
          t('blacklist-page.upload-confirm'),
          t('blacklist-page.upload-confirm-description'),
          () => Promise.resolve(),
          t('general.upload')
        );

        if (!confirmed) return;
        const formData = new FormData();
        formData.append('csv', data.file);
        const response = await BlackListService.addNumbersBlackListCsv(formData, payload?._id);

        const html = (
          <div className=" text-start">
            <h2 className="fs-2 fw-bold mb-4">Resumen:</h2>

            <div className="d-flex flex-column gap-2 fs-5">
              <p className="mb-0">
                Números agregados:{' '}
                <strong className="text-info fw-bolder fs-4">{response.totalNumbers}</strong>
              </p>
              {response.invalidNumbers && response.invalidNumbers.length > 0 && (
                <p className="mb-0">
                  Números inválidos:{' '}
                  <strong className="text-danger fw-bolder">
                    {response.invalidNumbers.length}
                  </strong>
                  <a
                    onClick={handleDownloadInvalidNumbers(response.invalidNumbers)}
                    className="ms-2 text-decoration-underline text-danger fw-bolder cursor-pointer"
                  >
                    Ver números
                  </a>
                </p>
              )}
            </div>
          </div>
        );

        AlertService.success(<p className="h2">¡ Lista negra actualizada !</p>, html);

        reset(defaultValues);
        getFile(0);
        LogData({ blacklist: payload?._id, csv: formData }, buttonId, window.location.href);
      } catch (errorCode: any) {
        console.log('Error: ', errorCode);
        AlertService.toastError(errorCode.error || errorCode);
      }
    } else {
      try {
        const newData = { blacklist: payload?._id, phone_number: data.phone_number };
        await BlackListService.saveNumber(newData);
        resetField('phone_number');
        AlertService.toastSuccess(t('blacklist-page.success-update-phone'));
        LogData(newData, buttonId, window.location.href);
        getFile(currentPage);
      } catch (errorCode: any) {
        AlertService.toastError(errorCode.error || errorCode);
        console.error('Error obteniendo empresas:', errorCode);
      }
    }
  };

  const deleteNumberBlackList = async (data: any, buttonId: string) => {
    const confirmed = await AlertService.warning(
      t('general.confirmation'),
      t('blacklist-page.confirm-delete', { number: data?.number }),
      () => Promise.resolve(),
      t('general.delete')
    );

    if (!confirmed) return;
    try {
      await BlackListService.deleteNumber(payload?._id, data?._id);
      AlertService.toastSuccess(t('blacklist-page.success-delete'));
      getFile(currentPage);
      LogData({ blacklist: payload?._id, numberId: data?._id }, buttonId, window.location.href);
    } catch (errorCode: any) {
      AlertService.toastError(errorCode.error || errorCode);
      console.error('Error obteniendo empresas:', errorCode);
    }
  };

  const filteredRows = useMemo(() => {
    return rows.filter((row: any) =>
      Object.values(row).some((value) =>
        value?.toString().toLowerCase().includes(term.toLowerCase())
      )
    );
  }, [rows, term]);

  const totalPages = Math.ceil(totalRows / rowsPerPage);

  const handleNextPage = () => {
    if (currentPage < totalPages - 1) {
      setCurrentPage(currentPage + 1);
    }
  };

  const handlePrevPage = () => {
    if (currentPage > 0) {
      setCurrentPage(currentPage - 1);
    }
  };

  const handlePageClick = (page: number) => {
    setCurrentPage(page);
  };

  const visiblePages = useMemo(() => {
    const visiblePageCount = 3;
    const start = Math.max(0, Math.min(currentPage - 1, totalPages - visiblePageCount));
    const end = Math.min(totalPages, start + visiblePageCount);
    return Array.from({ length: end - start }, (_, i) => start + i);
  }, [currentPage, totalPages]);

  useEffect(() => {
    if (!payload) {
      AlertService.info(t('general.atention'), t('blacklist-page.alert-payload'));
      navigate('/blacklist');
    }
  }, [payload, navigate]);
  if (!payload) {
    return null;
  }

  useEffect(() => {
    if (payload) {
      getFile(currentPage);
    }
  }, [payload, currentPage]);

  useEffect(() => {
    if (rowsPerPage !== 0) {
      getFile(0);
    }
  }, [rowsPerPage]);

  return (
    <>
      <Navigate
        title={t('roles.btn-edit-modal')}
        titleBolder={payload?.name}
        subtitle=""
        breadcrumbItems={[t('asaid.link-blacklist'), t('general.edit')]}
        btnBack
        route="/blacklist"
      />
      <div className="row gy-5 g-xl-10">
        <div className="col-xl-7 mb-5 mb-xl-10">
          <div className="card mb-5 mb-xxl-8">
            <div className="card-header align-items-center border-0 p-5">
              <div className="mt-6">
                <Title
                  icon="ki-notepad-edit"
                  title={`${t('blacklist-page.numbers-registered')}: ${totalRegisters.toLocaleString()}`}
                  subtitle={t('blacklist-page.edit-blacklist-subtitle')}
                  type="primary"
                />
              </div>
            </div>
            <div className="card-body">
              <div className="flex-column current" data-kt-stepper-element="content">
                <div className="pb-8 pb-lg-10">
                  <div className="d-flex justify-content-between mb-10">
                    <div className="d-flex align-items-center position-relative my-1 me-auto">
                      <Controller
                        control={control}
                        name="registers"
                        rules={{ required: true }}
                        render={({ field: { onChange, value }, fieldState: { error } }) => (
                          <>
                            <Select
                              options={options}
                              noOptionsMessage={({ inputValue }) =>
                                inputValue
                                  ? t('general.no-options', { inputValue })
                                  : t('general.no-options')
                              }
                              className={
                                error ? 'form-select-custom is-invalid' : 'form-select-custom'
                              }
                              classNamePrefix="form-select-custom"
                              placeholder={t('blacklist-page.company-placeholder')}
                              onChange={(selectedOption) => {
                                const selectedValue = selectedOption
                                  ? (selectedOption as { value: number }).value
                                  : '';
                                onChange(selectedValue);
                                setRowsPerPage((selectedOption as { value: number }).value);
                              }}
                              value={options.find((option: any) => option.value === value) || ''}
                            />
                            <InvalidFeedback error={error} />
                          </>
                        )}
                      />
                    </div>
                    <div className="d-flex align-items-center position-relative my-1 ms-auto">
                      <i className="ki-duotone ki-magnifier fs-3 position-absolute ms-4">
                        <span className="path1" />
                        <span className="path2" />
                      </i>
                      <input
                        className="form-control form-control-solid w-250px ps-12"
                        placeholder={t('datatable.input-search')}
                        type="text"
                        disabled={isRequesting}
                        value={term}
                        maxLength={10}
                        onChange={({ target: { value } }) => {
                          const onlyNumbers = value.replace(/\D/g, '');
                          setTerm(onlyNumbers.slice(0, 10));
                        }}
                      />
                    </div>
                  </div>
                  {isRequesting ? (
                    <div className="text-center relative">
                      <div className="loader"></div>
                    </div>
                  ) : filteredRows.length === 0 ? (
                    <div className="text-center pt-20 h-400px">
                      <img
                        alt="imageCh"
                        title="empty-table"
                        className="mb-3 theme-light-show"
                        src="/assets/media/illustrations/front/oc-thinking.svg"
                        width="50%"
                      />
                      <img
                        alt="imageCh"
                        title="empty-table"
                        className="mb-3 theme-dark-show"
                        src="/assets/media/illustrations/front/oc-thinking-dark.svg"
                        width="50%"
                      />
                      <p className="mb-0 fs-3">{t('datatable.empty-records')}</p>
                    </div>
                  ) : (
                    <div className="table-responsive mh-md-400px">
                      <table className="table table-row-bordered table-rounded border gy-5 gs-7">
                        <thead className="bg-light sticky-top">
                          <tr className="fw-semibold fs-6 text-gray-800 border-bottom border-gray-200">
                            {columns.map((column, index) => (
                              <th
                                key={index}
                                className={
                                  column.name === t('general.acctions')
                                    ? `${style.btnContainer}`
                                    : ''
                                }
                              >
                                {column.name}
                              </th>
                            ))}
                          </tr>
                        </thead>
                        <tbody>
                          {filteredRows.map((row: any, index: any) => (
                            <tr key={index}>
                              {columns.map((column, colIndex) => (
                                <td key={colIndex}>{column.cell(row)}</td>
                              ))}
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                  {filteredRows.length > 0 && (
                    <nav className="p-4 d-flex justify-content-between align-items-center mt-10">
                      <div className="pagination-container d-flex align-items-center w-100 justify-content-end">
                        <div className="d-flex align-items-center">
                          <button
                            title="btn-pagination"
                            type="button"
                            className="btn btn-sm btn-icon btn-active-light-info mx-1"
                            onClick={handlePrevPage}
                            disabled={currentPage === 0}
                          >
                            <i className="bi bi-chevron-left fs-3"></i>
                          </button>
                          {visiblePages.map((page) => (
                            <button
                              key={page}
                              type="button"
                              className={`btn btn-sm btn-icon mx-1 ${page === currentPage ? 'btn-info' : 'btn-secondary'}`}
                              onClick={() => handlePageClick(page)}
                            >
                              {page + 1}
                            </button>
                          ))}
                          <button
                            title="btn-pagination"
                            type="button"
                            className="btn btn-sm btn-icon btn-active-light-info mx-1"
                            onClick={handleNextPage}
                            disabled={currentPage === totalPages - 1}
                          >
                            <i className="bi bi-chevron-right fs-3"></i>
                          </button>
                        </div>
                      </div>
                    </nav>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
        <div className="col-xl-5 mb-xl-10">
          <div className="card mb-5 mb-xxl-8">
            <div className="card-body pt-1">
              <div className="col-12 mb-10 mb-lg-0 h-100">
                <form
                  onSubmit={handleSubmit((data) => submitNumber(data, 'editBlackList-8349657902'))}
                >
                  <div className="fv-row mb-1">
                    <div className="align-items-center border-0 mt-5 mb-3">
                      <div className="mt-6">
                        <Title
                          icon="ki-file-up"
                          title={t('blacklist-page.add-numbers-title')}
                          subtitle={t('blacklist-page.add-numbers-subtitle')}
                          type="primary"
                        />
                      </div>
                    </div>
                    <div className="d-flex flex-stack cursor-pointer mb-3">
                      <div className="d-flex align-items-center mb-5">
                        <span className="symbol me-e6 symbol-label"></span>
                        <div className="d-flex flex-column">
                          <span className="fw-bolder fs-3">{t('blacklist-page.massive-load')}</span>
                          <span className="fs-6 text-muted mw-250px">
                            <span className="fs-7 text-muted">
                              Activa esta opcion si quieres agregar más de un número a la vez
                            </span>
                          </span>
                        </div>
                      </div>
                      <span className="form-check form-check-custom form-check-solid">
                        <Switch
                          name="type"
                          checked={type}
                          value="1"
                          onChange={() => setType((prevType) => !prevType)}
                        />
                      </span>
                    </div>
                  </div>
                  {type ? (
                    <>
                      {!file ? (
                        <Dropzone
                          onChange={updateFiles}
                          className="dropzone cursor-default animate-fadeInUp animate-delay-1"
                          accept=".csv"
                          maxFiles={1}
                        >
                          <div className="dz-mkssage">
                            <>
                              <i className="ki-duotone ki-file-up fs-5x text-primary me-4">
                                <span className="path1" />
                                <span className="path2" />
                              </i>
                              <div className="d-flex flex-center flex-grow-1 flex-wrap flex-md-nowrap">
                                <div className="mb-3 mb-md-0 fw-semibold">
                                  <h5 className="text-gray-900 fw-bold">
                                    {t('campaigns.upload-file')}
                                  </h5>
                                  <div className="fs-7 pe-7 text-info">
                                    {t('blacklist-page.upload-file-description')}
                                  </div>
                                </div>
                              </div>
                            </>
                          </div>
                        </Dropzone>
                      ) : (
                        <div className="dropzone cursor-default">
                          <div className="dz-g">
                            <i className="ki-duotone ki-file-added text-primary fs-5x text-primary me-4">
                              <span className="path1" />
                              <span className="path2" />
                            </i>
                            <div className="d-flex flex-column align-items-center flex-grow-1">
                              <div className="mb-3 mb-md-0 fw-semibold text-center">
                                <h5 className="text-gray-900 fw-bold">
                                  {t('blacklist-page.file-uploaded')}
                                </h5>
                                <div className="fs-7 text-info">
                                  {typeof file === 'object' && file !== null && 'name' in file
                                    ? (file as File).name
                                    : t('blacklist-page.no-file-uploaded')}
                                </div>
                              </div>
                              <a
                                className="btn btn-danger px-6 text-nowrap btn-sm mt-3"
                                href="#"
                                onClick={removeFile}
                              >
                                {t('general.delete')}
                              </a>
                            </div>
                          </div>
                        </div>
                      )}
                      <div className="d-flex align-items-start gap-4 mb-5 mt-10">
                        <span className="symbol me-3 symbol-label"></span>
                        <div className="d-flex flex-column">
                          <span className="fs-3 mb-2">
                            Ejemplo de archivo <strong className="text-info fw-bolder">CSV:</strong>
                          </span>
                          <img
                            src="/assets/media/examples/blacklist_example.png"
                            alt="Ejemplo de CSV"
                            className="img-fluid rounded-12 border"
                            width={'80%'}
                          />
                        </div>
                      </div>
                    </>
                  ) : (
                    <>
                      <div className="d-flex flex-column flex-md-row align-items-center">
                        <div className="flex-grow-1 me-md-3 mb-3 mb-md-0 animate-fadeInUp animate-delay-1">
                          <div className="position-relative">
                            <Input
                              label={t('blacklist-page.phone-number-label')}
                              requiredIndicator="required"
                              errors={errors?.phone_number}
                              onKeyDown={handleOnlyNumbers}
                              className={
                                'form-control form-control-solid' +
                                (errors.phone_number ? ' is-invalid' : '')
                              }
                              placeholder={t('blacklist-page.phone-number-placeholder')}
                              {...register('phone_number', {
                                required: true,
                                setValueAs: (value) => value.trim(),
                                pattern: {
                                  value: tenDigitsRegex,
                                  message: t('blacklist-page.phone-number-validation')
                                }
                              })}
                            />
                          </div>
                        </div>
                      </div>
                    </>
                  )}
                  <div
                    className={`d-flex justify-content-end ${errors?.phone_number ? 'mb-8' : ''}`}
                  >
                    <Button
                      type="submit"
                      id="sendData"
                      className="btn btn-primary hover-scale mt-10 ms-2 w-20 fw-bolder"
                      disabled={type ? !file : !isValid}
                      isLoading={isSubmitting}
                    >
                      {t('general.btn-add-modal')} {t('blacklist-page.phone-number-label')}
                    </Button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}

export default EditBlackList;
